/**
 * Tour Service - Manages guided tours for the Franchise Portal
 * 
 * Features:
 * - Auto-triggers tour on first visit to each page
 * - Stores completion status in localStorage
 * - Provides on-demand tour restart
 * - Allows skipping individual steps but not entire tour
 */

const TOUR_STORAGE_KEY = 'fos_tour_completed';

// Get completed tours from localStorage
export const getCompletedTours = () => {
  try {
    const stored = localStorage.getItem(TOUR_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    return {};
  }
};

// Mark a tour as completed
export const markTourCompleted = (tourId) => {
  try {
    const completed = getCompletedTours();
    completed[tourId] = {
      completedAt: new Date().toISOString(),
      version: 1 // Can be used to force re-show tours after updates
    };
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(completed));
  } catch (e) {
    console.error('Error saving tour completion:', e);
  }
};

// Check if a tour has been completed
export const isTourCompleted = (tourId) => {
  const completed = getCompletedTours();
  return !!completed[tourId];
};

// Reset all tours (for "Start Tour" button)
export const resetAllTours = () => {
  localStorage.removeItem(TOUR_STORAGE_KEY);
};

// Reset a specific tour
export const resetTour = (tourId) => {
  try {
    const completed = getCompletedTours();
    delete completed[tourId];
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(completed));
  } catch (e) {
    console.error('Error resetting tour:', e);
  }
};

// Tour definitions for each page
export const TOUR_DEFINITIONS = {
  // Dashboard Tour
  dashboard: {
    id: 'dashboard',
    name: 'Dashboard Overview',
    steps: [
      {
        target: '[data-testid="dashboard-welcome"]',
        content: 'Welcome to your Franchise Dashboard! This is your central hub for managing daily operations, viewing sales, and accessing key features.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-testid="today-sales-card"]',
        content: 'View your today\'s sales here. The amount is hidden by default for privacy. Click the eye icon to reveal it (requires your PIN).',
        placement: 'bottom',
      },
      {
        target: '[data-testid="quick-actions"]',
        content: 'Quick Actions help you get things done fast! Generate new orders or track expenses with just one click.',
        placement: 'left',
      },
      {
        target: '[data-testid="recent-orders"]',
        content: 'Recent orders appear here. Click on any order to view full details, reprint receipts, or delete if needed.',
        placement: 'top',
      },
      {
        target: '[data-testid="start-tour-btn"]',
        content: 'You can restart any tour at any time by clicking this help button!',
        placement: 'bottom',
      },
    ],
  },

  // View Eye (Sales Visibility) Tour
  viewEye: {
    id: 'viewEye',
    name: 'Sales Visibility',
    steps: [
      {
        target: '[data-testid="today-sales-card"]',
        content: 'Your sales amount is hidden by default for security. Only authorized personnel with the PIN can view it.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-testid="today-sales-card"] button',
        content: 'Click the eye icon to reveal today\'s sales. You\'ll need to enter your franchise PIN to verify your identity.',
        placement: 'bottom',
      },
      {
        target: '[data-testid="today-sales-card"]',
        content: 'Once revealed, sales will auto-hide after 10 seconds for security. You can also click the eye icon again to hide it manually.',
        placement: 'bottom',
      },
    ],
  },

  // Generate Order Tour
  generateOrder: {
    id: 'generateOrder',
    name: 'Order Generation',
    steps: [
      {
        target: '[data-testid="menu-categories"]',
        content: 'Browse menu items by category. Click on a category tab to filter items quickly.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-testid="menu-item-grid"]',
        content: 'Click on any item to add it to your cart. Each item shows its price and tax information (like "+5% GST" or "incl 18% GST").',
        placement: 'right',
      },
      {
        target: '[data-testid="cart-section"]',
        content: 'Your cart shows the total amount. The cart total updates as you add items.',
        placement: 'top',
      },
      {
        target: '[data-testid="advanced-billing-toggle"]',
        content: 'Enable Advanced Billing for detailed invoices with GST breakdown, customer details, and order type selection (Dine-in, Takeaway, Delivery).',
        placement: 'bottom',
      },
      {
        target: '[data-testid="payment-button"]',
        content: 'Click here to select payment method and optionally tag the bill to a customer ledger for credit tracking.',
        placement: 'top',
      },
      {
        target: '[data-testid="place-order-btn"]',
        content: 'Once ready, click Save to place the order. A receipt will be generated automatically for printing.',
        placement: 'top',
      },
    ],
  },

  // Raw Material Ordering Tour
  rawMaterialOrder: {
    id: 'rawMaterialOrder',
    name: 'Raw Material Ordering',
    steps: [
      {
        target: '[data-testid="raw-material-header"]',
        content: 'Welcome to Raw Material Ordering! Here you can order supplies from your admin/HQ and track delivery status.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-testid="raw-material-items"]',
        content: 'Browse available raw materials. Each item shows the unit (kg, liters, pieces) and you can enter the quantity needed.',
        placement: 'bottom',
      },
      {
        target: '[data-testid="raw-material-cart"]',
        content: 'Selected items appear here. Review quantities and total before placing your order.',
        placement: 'left',
      },
      {
        target: '[data-testid="raw-material-history"]',
        content: 'View your order history with delivery and payment status. You can track when orders are dispatched and delivered.',
        placement: 'top',
      },
    ],
  },

  // Ledger Management Tour
  ledgerManagement: {
    id: 'ledgerManagement',
    name: 'Ledger Management',
    steps: [
      {
        target: '[data-testid="add-party-trigger"]',
        content: 'Add a new party (customer) to your ledger. Each party gets a credit account where you can track what they owe.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '.party-list',
        content: 'All your parties are listed here with their current balance. Green balance means they have credit, red means they owe you.',
        placement: 'top',
      },
      {
        target: '[data-testid^="add-money-"]',
        content: 'Click "Add Money" when a party makes a payment. This reduces their outstanding balance.',
        placement: 'left',
      },
      {
        target: '[data-testid^="view-history-"]',
        content: 'View complete transaction history for any party - all orders tagged to them and all payments received.',
        placement: 'left',
      },
      {
        target: '[data-testid^="whatsapp-toggle-"]',
        content: 'Enable WhatsApp notifications to automatically send balance updates to the party after each transaction.',
        placement: 'left',
      },
    ],
  },

  // Reports Tour
  reports: {
    id: 'reports',
    name: 'Sales Reports',
    steps: [
      {
        target: '[data-testid="franchise-report-start-input"]',
        content: 'Select the start date for your report. You can generate reports for any date range.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '[data-testid="franchise-report-end-input"]',
        content: 'Select the end date. For daily reports, use the same date. For weekly/monthly, select a range.',
        placement: 'bottom',
      },
      {
        target: '[data-testid="franchise-generate-report-button"]',
        content: 'Click Generate Report to view detailed sales data including item-wise breakdown, payment methods, and totals.',
        placement: 'top',
      },
      {
        target: '[data-testid="franchise-report-grand-total"]',
        content: 'The grand total shows your revenue for the selected period. Below you\'ll see item-wise sales breakdown.',
        placement: 'top',
      },
    ],
  },

  // Attendance Tour
  attendance: {
    id: 'attendance',
    name: 'Attendance System',
    steps: [
      {
        target: '.attendance-camera',
        content: 'Staff members punch in and out using face recognition. Position your face in the camera frame.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '.attendance-staff-select',
        content: 'Select your name from the staff list. The system will verify your face against your enrolled photo.',
        placement: 'bottom',
      },
      {
        target: '.attendance-punch-btn',
        content: 'Click Punch In when arriving or Punch Out when leaving. The system records exact timestamps.',
        placement: 'top',
      },
      {
        target: '.attendance-status',
        content: 'Your attendance status shows if you\'re punched in or out, along with today\'s working hours.',
        placement: 'top',
      },
    ],
  },

  // Staff Management Tour (accessed via PIN)
  staffManagement: {
    id: 'staffManagement',
    name: 'Staff Management',
    steps: [
      {
        target: '.staff-tabs',
        content: 'Staff Management has three sections: Staff List, Attendance Records, and Payroll.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '.staff-list-section',
        content: 'View all your staff members with their role, shift type, and status. Click on any staff to view/edit details.',
        placement: 'bottom',
      },
      {
        target: '.add-staff-btn',
        content: 'Add new staff through a 4-step process: Basic Info, Shift Config, Salary Setup, and Face Enrollment.',
        placement: 'bottom',
      },
      {
        target: '.attendance-records-tab',
        content: 'View daily attendance records with punch times, late arrivals, early leaves, and overtime hours.',
        placement: 'bottom',
      },
      {
        target: '.payroll-tab',
        content: 'Generate monthly payroll with automatic calculations for salary, deductions, and overtime pay.',
        placement: 'bottom',
      },
    ],
  },
};

// Default Joyride styles
export const TOUR_STYLES = {
  options: {
    primaryColor: '#D97706', // Amber to match FOS theme
    zIndex: 10000,
    arrowColor: '#fff',
    backgroundColor: '#fff',
    textColor: '#1f2937',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
  },
  tooltip: {
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
  },
  tooltipContainer: {
    textAlign: 'left',
  },
  tooltipTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  tooltipContent: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#4b5563',
  },
  buttonNext: {
    backgroundColor: '#D97706',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
  },
  buttonBack: {
    color: '#6b7280',
    marginRight: '8px',
    fontSize: '14px',
  },
  buttonClose: {
    display: 'none', // Hide close button, must complete tour
  },
  buttonSkip: {
    color: '#9ca3af',
    fontSize: '13px',
  },
  spotlight: {
    borderRadius: '8px',
  },
};

// Custom locale for tour buttons
export const TOUR_LOCALE = {
  back: 'Back',
  close: 'Close',
  last: 'Got it!',
  next: 'Next',
  skip: 'Skip this step',
  open: 'Open the dialog',
};

export default {
  getCompletedTours,
  markTourCompleted,
  isTourCompleted,
  resetAllTours,
  resetTour,
  TOUR_DEFINITIONS,
  TOUR_STYLES,
  TOUR_LOCALE,
};
